import { DateTimeBox, DateTimeBoxLayoutStyle } from '@apply/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useStandardLoanStore } from '../../../../../../state/standardLoanStore';
import { isRequiredOnceNoteDated } from '../functions/isRequiredOnceNoteDated';
import { monthDiff } from '../../../../../../../../../../common/functions/monthDiff';
import { SetupForm, SetupLoanContext } from '../constants/setupForm';
import { parseSetupDate } from '../functions/parseSetupDate';
import '../styles/_maturityDateField.scss';

/**
 * Calculated from Note Date + Initial Term, but can be changed by hand (not for sub notes). Warns when it no longer
 * matches the loan terms.
 */
export const MaturityDateField = () => {
    /** Warning when the Maturity Date doesn't match Note Date + Initial Term; undefined when there's nothing to warn about. */
    const getMaturityDateWarning = (
        form: SetupForm,
        context: SetupLoanContext,
        isAutoCalculated: boolean
    ): string | undefined => {
        const noteDate = parseSetupDate(form.noteDate);
        const maturityDate = parseSetupDate(form.maturityDate);
        if (isAutoCalculated || context.isSubNote || !noteDate || !form.initialTermMonths || !maturityDate) {
            return undefined;
        }
        const months = monthDiff(noteDate, maturityDate) ?? 0;
        if (months === form.initialTermMonths) {
            return undefined;
        }
        return months > form.initialTermMonths
            ? 'Maturity Date Is Past the Loan Terms.'
            : 'Maturity Date Is Less than the Loan Terms.';
    };

    const setup = useStandardLoanStore((state) => state.setup);
    const context = useStandardLoanStore((state) => state.setupLoanContext);
    const maturityDateAutoCalculated = useStandardLoanStore((state) => state.maturityDateAutoCalculated);
    const updateSetup = useStandardLoanStore((state) => state.updateSetup);

    const warning = getMaturityDateWarning(setup, context, maturityDateAutoCalculated);
    // Used for the box's label and (via the CSS variable) to line the warning up under the box
    const labelWidth = '100px';

    return (
        <div
            className='maturityDateField'
            style={{ '--maturityDateField-label-width': labelWidth } as React.CSSProperties}
        >
            <DateTimeBox
                id='setupMaturityDate'
                value={setup.maturityDate}
                // updateSetup treats a Maturity Date passed in as typed by hand, so the terms warning applies again
                onChange={(maturityDate) => updateSetup({ maturityDate })}
                // DateTimeBox doesn't call onChange when the date is cleared (or an invalid date is rejected); it only
                // fails validation. Treat a failed validation as "no date".
                onValidationEvent={(isValid) => {
                    if (!isValid) {
                        updateSetup({ maturityDate: undefined });
                    }
                }}
                label='Maturity Date'
                required={isRequiredOnceNoteDated(setup)}
                readOnly={context.isSubNote}
                width='160px'
                placeholder='MM/DD/YYYY'
                height='36px'
                layoutStyle={DateTimeBoxLayoutStyle.Horizontal}
                labelWidth={labelWidth}
            />
            {warning && (
                <div className='maturityDateField-warning'>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    {warning}
                </div>
            )}
        </div>
    );
};
